import API_CONFIG from "../../../config/apiconfig";

const WORD_FORM_EXTENSIONS = new Set(["doc", "docx"]);
const BLOB_HOST_PATTERN = /\.blob\.core\.windows\.net$/i;

const ONLYOFFICE_ERROR_MESSAGES = {
  "-1": "Unknown error.",
  "-2": "Could not load OnlyOffice DocsAPI script.",
  "-3": "DocsAPI is not defined.",
  "-4": "Document download failed (Document Server cannot reach the file URL).",
  "-6": "Document conversion failed.",
  "-20": "JWT token missing or invalid on Document Server.",
  "-82": "Document Server could not open the file (bad URL, format, or access).",
};

/** Word documents — extension hint only; prefer metadata when available. */
export function isWordFormDocumentExtension(extension) {
  const ext = String(extension ?? "")
    .trim()
    .replace(/^\./, "")
    .toLowerCase();
  return WORD_FORM_EXTENSIONS.has(ext);
}

/** Show Download original when metadata says Word form, or extension is Word with no metadata yet. */
export function shouldOfferOriginalWordDownload({ extension, metadata } = {}) {
  if (metadata && typeof metadata === "object") {
    if (metadata.isWordForm === true || metadata.documentKind === "wordForm") {
      return true;
    }
    if (metadata.isWordForm === false && metadata.documentKind === "wordDocument") {
      return isWordFormDocumentExtension(extension);
    }
  }
  return isWordFormDocumentExtension(extension);
}

function getBlobBaseUrl() {
  return String(API_CONFIG.blobBaseURL || "").replace(/\/+$/, "");
}

/** Fix accidental double blob prefix from legacy saves. */
function dedupeBlobUrlPrefix(url) {
  const normalized = String(url || "").trim();
  const base = getBlobBaseUrl();
  if (!normalized || !base) return normalized;

  const baseHostPath = base.replace(/^https?:\/\//i, "");
  const duplicatePrefix = `${base}/${baseHostPath}/`;
  if (normalized.toLowerCase().startsWith(duplicatePrefix.toLowerCase())) {
    return `${base}/${normalized.slice(duplicatePrefix.length)}`;
  }

  return normalized;
}

function withHttpsIfHostPath(raw) {
  const trimmed = String(raw || "").trim().replace(/^\/+/, "");
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return dedupeBlobUrlPrefix(trimmed);
  if (trimmed.startsWith("//")) return dedupeBlobUrlPrefix(`https:${trimmed}`);
  if (/^[a-z0-9.-]+\.(blob\.core\.windows\.net|azurewebsites\.net)\//i.test(trimmed)) {
    return dedupeBlobUrlPrefix(`https://${trimmed}`);
  }
  return "";
}

/** Resolve stored DocumentURL to a full blob URL. */
export function buildQmsDocumentDownloadUrl(documentUrl) {
  const raw = String(documentUrl ?? "").trim();
  if (!raw) return "";

  const absolute = withHttpsIfHostPath(raw);
  if (absolute) return absolute;

  const base = getBlobBaseUrl();
  const path = raw.replace(/^\/+/, "");
  return dedupeBlobUrlPrefix(`${base}/${path}`);
}

/**
 * Encode blob/document URLs so OnlyOffice Document Server can fetch them
 * (spaces and other reserved characters in blob paths break server-side download).
 */
export function encodeDocumentUrlForViewer(documentUrl) {
  const resolved = buildQmsDocumentDownloadUrl(documentUrl);
  if (!resolved) return "";

  try {
    const url = new URL(resolved);
    url.pathname = url.pathname
      .split("/")
      .map((segment) => {
        if (!segment) return segment;
        try {
          return encodeURIComponent(decodeURIComponent(segment));
        } catch {
          return encodeURIComponent(segment);
        }
      })
      .join("/");
    return url.toString();
  } catch {
    return resolved.replace(/ /g, "%20");
  }
}

export function isPublicBlobDocumentUrl(url) {
  try {
    const parsed = new URL(String(url || "").trim());
    return (
      (parsed.protocol === "https:" || parsed.protocol === "http:") &&
      BLOB_HOST_PATTERN.test(parsed.hostname)
    );
  } catch {
    return false;
  }
}

function onlyOfficeFetchUrl(resolvedUrl) {
  if (!resolvedUrl) return "";
  try {
    const parsed = new URL(resolvedUrl);
    const pathNeedsEncoding = parsed.pathname
      .split("/")
      .some((segment) => segment && segment !== decodeURIComponent(segment));
    const hasSpaces = /\s/.test(parsed.pathname);
    if (hasSpaces || pathNeedsEncoding) {
      return encodeDocumentUrlForViewer(resolvedUrl);
    }
    return resolvedUrl;
  } catch {
    return encodeDocumentUrlForViewer(resolvedUrl);
  }
}

/**
 * URL passed to OnlyOffice document.url — Document Server must fetch this server-side.
 */
export function buildQmsOnlyOfficeDocumentUrl(documentUrl) {
  const resolvedUrl = buildQmsDocumentDownloadUrl(documentUrl);
  const blobUrl = onlyOfficeFetchUrl(resolvedUrl);
  if (!blobUrl) {
    return { url: "", blobUrl: "", source: "missing", error: "Document URL is missing." };
  }

  if (!/^https?:\/\//i.test(blobUrl)) {
    return {
      url: "",
      blobUrl,
      source: "invalid",
      error:
        "Document URL is not a full https link. OnlyOffice cannot open relative paths — check DocumentURL in the database.",
    };
  }

  if (!isPublicBlobDocumentUrl(blobUrl)) {
    return {
      url: "",
      blobUrl,
      source: "invalid",
      error:
        "Document URL must point to ascensionblob (public https). Re-upload the file or fix DocumentURL in the database.",
    };
  }

  // Same as eISF: OnlyOffice Document Server fetches public blob URLs directly.
  return { url: blobUrl, blobUrl, source: "blob-direct", error: "" };
}

export function describeOnlyOfficePreviewError(errorCode) {
  const key = String(errorCode ?? "").trim();
  return ONLYOFFICE_ERROR_MESSAGES[key] || "OnlyOffice preview failed.";
}

/** Trigger browser download/open of the original uploaded file (not OnlyOffice export). */
export function downloadQmsOriginalDocument({ documentUrl, fileName }) {
  const url = buildQmsDocumentDownloadUrl(documentUrl);
  if (!url) return false;

  const link = document.createElement("a");
  link.href = url;
  link.download = String(fileName || "document").trim() || "document";
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
}
